import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { create } from 'zustand';

// ─────────────────────────────────────────────────────────────
// Page Store — lets a page publish its title + primary action into
// the TopBar header. The TopBar shows the title on the left and the
// action on the right. Pages that publish nothing leave the bar's
// left side empty (and keep their own in-page header).
// ─────────────────────────────────────────────────────────────

interface PageStore {
  title: string | null;
  action: ReactNode | null;
  setTitle: (title: string | null) => void;
  setAction: (action: ReactNode | null) => void;
}

export const usePageStore = create<PageStore>((set) => ({
  title: null,
  action: null,
  setTitle: (title) => set({ title }),
  setAction: (action) => set({ action }),
}));

/**
 * Publish a TopBar title + action for the current page.
 * Clears both on unmount so the next page starts clean.
 * Pass `deps` so the action re-registers when handlers change.
 */
export function usePageHeader(title: string, action: ReactNode = null, deps: React.DependencyList = []) {
  const setTitle = usePageStore((s) => s.setTitle);
  const setAction = usePageStore((s) => s.setAction);
  useEffect(() => {
    setTitle(title);
    setAction(action);
    return () => {
      setTitle(null);
      setAction(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTitle, setAction, title, ...deps]);
}
